import ReactPlayer from "react-player";
import {useNavigate} from "react-router-dom";
import {useEffect, useState, useRef} from "react";
import {AppFlex} from "shared/UI/UIKit/Flex";
import {AppText} from "shared/UI/UIKit/Text";
import {AppButton} from "shared/UI/UIKit/Button";
import {AppImage} from "shared/UI/UIKit/Image";
import {AppVolumeIcon} from "shared/UI/UIKit/icons/Volume";
import {getMethodologicalModuleUrl} from "services/navigation";
import type {
  PracticalModulePage as PracticalModulePageType,
  PracticalModuleStep as PracticalModuleStepType
} from "../../../../config/types";
import styles from "./styles.module.scss";

type Props = {practicalModule: PracticalModulePageType}

export const FirstPracticalModule = ({practicalModule: module}: Props) => {
  const maxStepValue = 3;
  const navigate = useNavigate();
  const audioElement = useRef<HTMLAudioElement | null>(null);
  const [step, setStep] = useState(0);
  const [currentStep, setCurrentStep] = useState<PracticalModuleStepType>();
  const [isPlaying, setIsPlaying] = useState(true);

  if (!module) {
    return null;
  }

  useEffect(() => {
    setCurrentStep(module.steps[step]);

	return () => {
      setStep(0);
      setCurrentStep(module.steps[0]);
      setIsPlaying(false);
	};
  }, []);

  const goToPrevStep = () => {
    if (step === 0) {
      navigate(getMethodologicalModuleUrl({id: "11"}));
    } else {
      setStep(step - 1);
      setCurrentStep(module.steps[step - 1]);
    }
  };

  const goToNextStep = () => {
    if (step < maxStepValue) {
      setStep(step + 1);
      setCurrentStep(module.steps[step + 1]);
	} else {
      // navigate(getMethodologicalModuleUrl({id: (+module.id + 1).toString()}));
      navigate(getMethodologicalModuleUrl({id: "3"}));
	}
  };

  const togglePlay = () => {
    if (audioElement) {
      if (isPlaying) {
        setIsPlaying(false);
        audioElement?.current?.pause();
      } else {
        setIsPlaying(true);
        audioElement?.current?.play();
      }
    }
  };

  return (
    <AppFlex className={styles.step}>
      {currentStep?.audioUrl && <audio ref={audioElement} src={currentStep.audioUrl}></audio>}
      {step === 0 && (
        <AppFlex direction="row" className={styles.blockWithAudio}>
          <AppFlex>
            <AppVolumeIcon className={styles.volumeIcon} onClick={togglePlay}/>
          </AppFlex>
          <AppFlex>
            <AppText size={18}>Уважаемые коллеги!</AppText>
            <AppText size={18}>{currentStep?.text}</AppText>
            <ol className={styles.list}>
              {currentStep?.questions && currentStep.questions.map((question, idx) => (
                <li key={"question" + idx} className={styles.listItem}><AppText size={18}>{question}</AppText></li>
              ))}
            </ol>
            <AppText className={styles.videoLabel} size={18}>Видеофрагмент урока</AppText>
            <ReactPlayer
              className={styles.video}
              controls
              config={{
                file: {
                  attributes: {
                    disablePictureInPicture: true,
                    controlsList: "nodownload noremoteplayback noplaybackrate"
                  }
                }
              }}
              url={currentStep?.videoUrl}/>
            <div className={styles.action}>
              <AppButton className={styles.prevStepBtn} onClick={goToPrevStep}>Назад</AppButton>
              <AppButton className={styles.nextStepBtn} onClick={goToNextStep}>Далее</AppButton>
            </div>
          </AppFlex>
        </AppFlex>
      )}
      {step === 1 && (
        <AppFlex className={styles.step}>
          <AppText size={18}>Уважаемые коллеги!</AppText>
          <AppText size={18}>{currentStep?.text}</AppText>
          <ol className={styles.list}>
            {currentStep?.questions && currentStep.questions.map((question, idx) => (
              <li key={"question" + idx} className={styles.listItem}>
                <AppText size={18} className={styles.boldText}>{question}</AppText>
              </li>
            ))}
          </ol>
          <AppImage src={currentStep?.imagePath} alt="image" className={styles.image}/>
          <AppText size={18} className={styles.additionalText}>{currentStep?.additionalText}</AppText>
          <div className={styles.action}>
            <AppButton className={styles.prevStepBtn} onClick={goToPrevStep}>Назад</AppButton>
            <AppButton className={styles.nextStepBtn} onClick={goToNextStep}>Продолжить</AppButton>
          </div>
        </AppFlex>
      )}
      {step === 2 && (
        <AppFlex className={styles.step} direction="row">
          <AppFlex>
            <AppVolumeIcon className={styles.volumeIcon} onClick={togglePlay}/>
          </AppFlex>
          <AppFlex>
            <AppText size={18}>{currentStep?.text}</AppText>
            <ol className={styles.list}>
              {currentStep?.questions && currentStep.questions.map((question, idx) => (
                <li key={"question" + idx} className={styles.listItem}><AppText size={18}>{question}</AppText></li>
              ))}
            </ol>
            <AppText className={styles.videoLabel} size={18}>Видеофрагмент урока</AppText>
            <ReactPlayer
              className={styles.video}
              controls
              config={{
                file: {
                  attributes: {
                    disablePictureInPicture: true,
                    controlsList: "nodownload noremoteplayback noplaybackrate"
                  }
                }
              }}
              url={currentStep?.videoUrl}/>
            <div className={styles.action}>
              <AppButton className={styles.prevStepBtn} onClick={goToPrevStep}>Назад</AppButton>
              <AppButton className={styles.nextStepBtn} onClick={goToNextStep}>Далее</AppButton>
            </div>
          </AppFlex>
        </AppFlex>
      )}
      {step === 3 &&
        <AppFlex className={styles.step}>
          <AppText size={18} className={styles.boldText}>Согласны ли вы с таким выводом?</AppText>
          <AppText className={styles.italicText} size={18}>{currentStep?.text}</AppText>
          <AppImage src={currentStep?.imagePath} alt="image" className={styles.image}/>
          <div className={styles.action}>
            <AppButton className={styles.prevStepBtn} onClick={goToPrevStep}>Назад</AppButton>
            <AppButton className={styles.nextStepBtn} onClick={goToNextStep}>
              Продолжить
            </AppButton>
          </div>
        </AppFlex>
      }
    </AppFlex>
  );
};
